// ConfigurationSettings.tsx
import { useState, useEffect, useCallback } from 'react';

import { useTranslation } from 'react-i18next';
import { useOutletContext } from 'react-router-dom';

import { ConfigSelect, type ConfigSelectOption } from '../../components/shared/ConfigSelect';
import { ReactComponent as FlagEnIcon } from '../../react-icons/flags/flag_en.svg';
import { ReactComponent as FlagEsIcon } from '../../react-icons/flags/flag_es.svg';
import { ReactComponent as FlagItIcon } from '../../react-icons/flags/flag_it.svg';
import { fetchAppConfig } from '../../services/config';
import { showMessage } from '../../showMessage';
import { applyThemePreference, getSystemColorScheme } from '../../theme';
import {
  applyMapColorModePreference,
  normalizeMapColorMode,
  resolveMapColorScheme,
} from '../../utils/mapboxStyle';

import type { SettingsOutletContext } from './SettingsPage';
import type { ColorScheme, ThemePreference } from '../../theme';
import type { MapColorMode } from '../../utils/mapboxStyle';

import '../../styles/ConfigurationSettings.css';

const API_BASE = process.env.REACT_APP_API_BASE_URL ?? '';

interface Boat {
  id: number;
  name: string;
}

interface GetBoatsResponse {
  success: boolean;
  boats: Boat[];
}

interface UpdateSettingsResponse {
  success: boolean;
  message?: string;
}

// Type guards to validate API responses at runtime
const isGetBoatsResponse = (value: unknown): value is GetBoatsResponse => {
  if (typeof value !== 'object' || value === null) {
    return false;
  }
  const v = value as Record<string, unknown>;
  const successOk = typeof v.success === 'boolean';
  const boatsOk =
    Array.isArray(v.boats) &&
    v.boats.every(
      (b) =>
        typeof b === 'object' &&
        b !== null &&
        typeof (b as Record<string, unknown>).id === 'number' &&
        typeof (b as Record<string, unknown>).name === 'string',
    );
  return successOk && boatsOk;
};

const isUpdateSettingsResponse = (value: unknown): value is UpdateSettingsResponse => {
  if (typeof value !== 'object' || value === null) {
    return false;
  }
  const v = value as Record<string, unknown>;
  const successOk = typeof v.success === 'boolean';
  const messageOk = v.message === undefined || typeof v.message === 'string';
  return successOk && messageOk;
};

const UPDATE_SETTINGS_FAILURE_MSG_KEY = 'settings.app.updateError';
const NOTIFICATIONS_ERROR_KEY = 'notifications.error';
const NOTIFICATIONS_SUCCESS_KEY = 'notifications.success';
const ERRORS_INVALID_SERVER_RESPONSE_KEY = 'errors.invalidServerResponse';

const DEFAULT_CONFIG = {
  theme: 'dark' as ThemePreference,
  language: 'en',
  default_boat_id: '',
  share_data: false,
  allow_analytics: false,
  map_color_mode: 'default' as MapColorMode,
};

type ConfigState = typeof DEFAULT_CONFIG;
const areConfigsEqual = (a: ConfigState, b: ConfigState) =>
  a.theme === b.theme &&
  a.language === b.language &&
  a.default_boat_id === b.default_boat_id &&
  a.share_data === b.share_data &&
  a.allow_analytics === b.allow_analytics &&
  a.map_color_mode === b.map_color_mode;

const resolveThemeColorScheme = (theme: ThemePreference): ColorScheme =>
  theme === 'system' ? getSystemColorScheme() : theme;

const readStoredConfig = (): ConfigState => {
  if (typeof window === 'undefined') {
    return DEFAULT_CONFIG;
  }

  try {
    const raw = localStorage.getItem('appConfig');
    if (!raw) {
      return DEFAULT_CONFIG;
    }
    const parsed = JSON.parse(raw) as Partial<typeof DEFAULT_CONFIG>;
    const theme =
      parsed.theme === 'dark' || parsed.theme === 'light' || parsed.theme === 'system'
        ? parsed.theme
        : DEFAULT_CONFIG.theme;
    return {
      theme,
      language:
        typeof parsed.language === 'string' && parsed.language.trim().length > 0
          ? parsed.language
          : DEFAULT_CONFIG.language,
      default_boat_id:
        typeof parsed.default_boat_id === 'string' ? parsed.default_boat_id : DEFAULT_CONFIG.default_boat_id,
      share_data: parsed.share_data === true,
      allow_analytics: parsed.allow_analytics === true,
      map_color_mode: normalizeMapColorMode(parsed.map_color_mode),
    };
  } catch (error) {
    console.warn('Failed to parse stored appConfig for theme setup', error);
    return DEFAULT_CONFIG;
  }
};

export const ConfigurationSettings = () => {
  const { t, i18n } = useTranslation('common');
  const [config, setConfig] = useState<ConfigState>(() => readStoredConfig());
  const [initialConfig, setInitialConfig] = useState<ConfigState>(() => readStoredConfig());
  const [userBoats, setUserBoats] = useState<Boat[]>([]);
  const { setHasUnsavedChanges, registerRevertHandler } =
    useOutletContext<SettingsOutletContext>();

  const setConfigValue = <K extends keyof ConfigState>(key: K, newValue: ConfigState[K]) => {
    setConfig((previous) => ({ ...previous, [key]: newValue }));
  };

  const fetchUserBoats = useCallback(async () => {
    try {
      const token = localStorage.getItem('token');
      const headers: HeadersInit = {
        'Content-Type': 'application/json',
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      };
      const response = await fetch(`${API_BASE}/api/user/getBoats`, {
        method: 'POST',
        headers,
      });
      if (!response.ok) {
        console.error("Error fetching user's boats: HTTP", response.status);
        return;
      }
      const dataUnknown: unknown = await response.json();
      if (isGetBoatsResponse(dataUnknown)) {
        if (dataUnknown.success) {
          setUserBoats(dataUnknown.boats);
        }
      } else {
        console.error("Error parsing user's boats response: invalid shape");
      }
    } catch (error) {
      console.error("Error fetching user's boats:", error);
    }
  }, []);

  useEffect(() => {
    void (async () => {
      try {
        const cfg = await fetchAppConfig();
        if (cfg) {
          setConfig(cfg);
          setInitialConfig(cfg);
          setHasUnsavedChanges(false);
        }
      } catch (error) {
        console.error('Error fetching app config:', error);
      }
    })();
    // Get user's boats
    void fetchUserBoats();
  }, [fetchUserBoats, setHasUnsavedChanges]);

  useEffect(() => {
    registerRevertHandler(() => {
      setConfig(initialConfig);
      if (typeof window !== 'undefined') {
        try {
          localStorage.setItem('appConfig', JSON.stringify(initialConfig));
        } catch (error) {
          console.warn('Failed to persist reverted appConfig', error);
        }
      }
      setHasUnsavedChanges(false);
    });
    return () => {
      registerRevertHandler(null);
    };
  }, [initialConfig, registerRevertHandler, setHasUnsavedChanges]);

  useEffect(() => {
    setHasUnsavedChanges(!areConfigsEqual(config, initialConfig));
  }, [config, initialConfig, setHasUnsavedChanges]);

  useEffect(() => {
    applyThemePreference(config.theme);
  }, [config.theme]);

  useEffect(() => {
    applyMapColorModePreference(config.map_color_mode);
  }, [config.map_color_mode]);

  // Keep i18n language in sync with selected config
  useEffect(() => {
    const selected = config.language?.toLowerCase();
    const lang = selected === 'es' ? 'es' : selected === 'it' ? 'it' : 'en';
    if (i18n.language !== lang) {
      void i18n.changeLanguage(lang);
    }
  }, [config.language, i18n]);

  const themeOptions: ConfigSelectOption[] = [
    { value: 'dark', label: t('settings.app.theme.options.dark') },
    { value: 'light', label: t('settings.app.theme.options.light') },
    { value: 'system', label: t('settings.app.theme.options.system') },
  ];

  const mapColorModeOptions: ConfigSelectOption[] = [
    {
      value: 'default',
      label: t('settings.app.mapColorMode.options.default.label'),
      description: t('settings.app.mapColorMode.options.default.description'),
      icon: <span className="map-color-mode-swatch map-color-mode-swatch--default" />,
    },
    {
      value: 'light',
      label: t('settings.app.mapColorMode.options.light.label'),
      description: t('settings.app.mapColorMode.options.light.description'),
      icon: <span className="map-color-mode-swatch map-color-mode-swatch--light" />,
    },
    {
      value: 'dark',
      label: t('settings.app.mapColorMode.options.dark.label'),
      description: t('settings.app.mapColorMode.options.dark.description'),
      icon: <span className="map-color-mode-swatch map-color-mode-swatch--dark" />,
    },
    {
      value: 'inverted',
      label: t('settings.app.mapColorMode.options.inverted.label'),
      description: t('settings.app.mapColorMode.options.inverted.description'),
      icon: <span className="map-color-mode-swatch map-color-mode-swatch--inverted" />,
    },
  ];

  const languageOptions: ConfigSelectOption[] = [
    { value: 'en', label: t('settings.app.language.options.en'), icon: <FlagEnIcon /> },
    { value: 'es', label: t('settings.app.language.options.es'), icon: <FlagEsIcon /> },
    { value: 'it', label: t('settings.app.language.options.it'), icon: <FlagItIcon /> },
  ];

  const boatOptions: ConfigSelectOption[] = [
    { value: '', label: t('settings.app.defaultBoat.none') },
    ...userBoats.map((boat) => ({
      value: String(boat.id),
      label: boat.name,
    })),
  ];
  const resolvedMapColorScheme = resolveMapColorScheme(
    resolveThemeColorScheme(config.theme),
    config.map_color_mode,
  );
  const mapColorModeResultText =
    resolvedMapColorScheme === 'dark'
      ? t('settings.app.mapColorMode.result.dark', 'Resultado actual: mapa oscuro')
      : t('settings.app.mapColorMode.result.light', 'Resultado actual: mapa claro');

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    try {
      const token = localStorage.getItem('token');
      const headers: HeadersInit = {
        'Content-Type': 'application/json',
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      };
      const response = await fetch(`${API_BASE}/api/user/updateSettings`, {
        method: 'POST',
        headers,
        body: JSON.stringify({
          theme: config.theme === 'light' ? 0 : config.theme === 'dark' ? 1 : 2,
          language: config.language,
          default_boat_id: config.default_boat_id,
          share_data: config.share_data,
          allow_analytics: config.allow_analytics,
          map_color_mode: config.map_color_mode,
        }),
      });
      if (!response.ok) {
        showMessage('error', t(NOTIFICATIONS_ERROR_KEY), t(UPDATE_SETTINGS_FAILURE_MSG_KEY));
        return;
      }
      const dataUnknown: unknown = await response.json();
      if (isUpdateSettingsResponse(dataUnknown) && dataUnknown.success) {
        showMessage('success', t(NOTIFICATIONS_SUCCESS_KEY), t('settings.app.updateSuccess'));
        const cfg = await fetchAppConfig();
        if (cfg) {
          setConfig(cfg);
          setInitialConfig(cfg);
          setHasUnsavedChanges(false);
        }
      } else if (isUpdateSettingsResponse(dataUnknown) && !dataUnknown.success) {
        showMessage('error', t(NOTIFICATIONS_ERROR_KEY), dataUnknown.message ?? t(UPDATE_SETTINGS_FAILURE_MSG_KEY));
      } else {
        showMessage('error', t(NOTIFICATIONS_ERROR_KEY), t(ERRORS_INVALID_SERVER_RESPONSE_KEY));
      }
    } catch (error) {
      console.error('Error updating user settings:', error);
      showMessage('error', t(NOTIFICATIONS_ERROR_KEY), t(UPDATE_SETTINGS_FAILURE_MSG_KEY));
    }
  };

  return (
    <div className="settings-form-container-config">
      <h2 className="settings-form-title-config">{t('settings.app.title')}</h2>
      <form onSubmit={handleSubmit}>
        <div className="form-group-config">
          <label htmlFor="theme-select">{t('settings.app.theme.label')}</label>
          <ConfigSelect
            id="theme-select"
            value={config.theme}
            options={themeOptions}
            onChange={(selected) => setConfigValue('theme', selected as ThemePreference)}
          />
        </div>
        <div className="form-group-config">
          <label htmlFor="map-color-mode-select">{t('settings.app.mapColorMode.label')}</label>
          <ConfigSelect
            id="map-color-mode-select"
            value={config.map_color_mode}
            options={mapColorModeOptions}
            onChange={(selected) =>
              setConfigValue('map_color_mode', normalizeMapColorMode(selected))
            }
          />
          <p className="map-color-mode-result" aria-live="polite">
            {mapColorModeResultText}
          </p>
        </div>
        <div className="form-group-config">
          <label htmlFor="language-select">{t('settings.app.language.label')}</label>
          <ConfigSelect
            id="language-select"
            value={config.language}
            options={languageOptions}
            onChange={(selected) => setConfigValue('language', selected)}
          />
        </div>
        <div className="form-group-config">
          <label htmlFor="default-boat-select">{t('settings.app.defaultBoat.label')}</label>
          <ConfigSelect
            id="default-boat-select"
            value={config.default_boat_id}
            options={boatOptions}
            onChange={(selected) => setConfigValue('default_boat_id', selected)}
          />
        </div>
        <div className="button-container-config">
          <button type="submit" className="edit-btn-config">
            {t('settings.app.save')}
          </button>
        </div>
      </form>
    </div>
  );
};
